import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { clone, includes, omit, remove } from 'lodash-es';
import { FormPageTypeEnum, FormSettingsEnum, PreviewPageType, TemplateScopeType, TemplateType } from '@dev/ee-api/ee-api-models';
import { SimpleToggle, Typography } from '@dev/ee-components';

import { ModifyForm, getFormCreatorStore, SetFormErrors } from '@/store/formCreator/actions';
import { StartLoadingTemplateParams, StartLoadingTemplates, StartLoadingTemplate, getLandingPageList } from '@/store/landingPageList/actions';
import { checkIsUrlInvalid } from '@/services/tools/urlValidation/checkIsUrlInvalid';
import { FormErrorNames } from '@/store/formCreator/types';

import { usePageTypeValue } from '../hooks/usePageValue';
import { DisplayedScreenEnum, FormPageDropdownSection } from './FormPageDropdown';
import { ActivationEmailTemplateDropdown } from './ActivationEmailTemplateDropdown';
import { DEFAULT_OPTIONS_COUNT } from '..';
import { ToggleWrapper } from './styles';

export const DoubleOptInSection = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { formObject, formPayload, errors } = useSelector(getFormCreatorStore);
    const { template, templatesList } = useSelector(getLandingPageList);
    const pageType = usePageTypeValue(PreviewPageType.ActivationViaEmail);

    const isDoubleOptIn = (includes(formPayload?.FormSettings, FormSettingsEnum.AddDoubleOptIn) || includes(formObject?.FormSettings, FormSettingsEnum.AddDoubleOptIn) ||
        !includes(formObject?.FormSettings, FormSettingsEnum.AddDoubleOptIn) && !includes(formObject?.FormSettings, FormSettingsEnum.DoubleOptInDisabled)
    );
    const doubleOptInPage = formPayload?.DoubleOptInPage ?? formObject?.DoubleOptInPage;
    const [doubleOptIn, setDoubleOptIn] = useState(isDoubleOptIn);
    const [chosenLandingPage, setChosenLandingPage] = useState<any>([]);
    const [landingPageSearch, setLandingPageSearch] = useState(null);
    const [externalWebsite, setExternalWebsite] = useState(!checkIsUrlInvalid(doubleOptInPage) ? doubleOptInPage : null);
    const [isExternalWebsiteError, setExternalWebsiteError] = useState(false);

    const landingPagesParams: StartLoadingTemplateParams = {
        page: 1,
        search: landingPageSearch,
        limit: DEFAULT_OPTIONS_COUNT,
        type: [TemplateScopeType.Personal],
        templateTypes: [TemplateType.LandingPageEditor,TemplateType.LandingPageTemplate],
        isInfinityScroll: true,
    };

    useEffect((): void => {
        dispatch(StartLoadingTemplates(landingPagesParams));

        dispatch(ModifyForm({
            DoubleOptInPage: formObject?.DoubleOptInPage || null,
            ActivationTemplateName: formObject?.ActivationTemplateName || null
        }));

        if (includes(formObject?.FormPageType, FormPageTypeEnum.LandingPageDoubleOptInPage) && !!formObject?.DoubleOptInPage) {
            dispatch(StartLoadingTemplate(formObject?.DoubleOptInPage));
        }
    }, []);

    useEffect((): void => {
        setChosenLandingPage({label: template?.Name, value: template?.Name, link: template?.Link});
    }, [template]);

    const errorName = FormErrorNames.ACTIVATION_VIA_EMAIL_INVALID_URL;

    const resetError = (): void => {
        const errorsObject = clone(errors) || {};
        const newErrorsObject = omit(errorsObject, errorName);
        dispatch(SetFormErrors(newErrorsObject));
    }

    useEffect(() => {
        const errorsObject = clone(errors) || {};
        
        if (isExternalWebsiteError) {
            errorsObject[errorName] = true;
            dispatch(SetFormErrors(errorsObject));
            return;
        }
        resetError();
    }, [isExternalWebsiteError]);

    const changeDoubleOptInToggle = (ev): void => {
        const enabledDoubleOptIn = ev?.target?.checked;
        const formSettings = formPayload?.FormSettings ?? formObject?.FormSettings ?? [];
        const newFormSettings = remove(
            [...formSettings],
            setting => setting !== (enabledDoubleOptIn ? FormSettingsEnum.DoubleOptInDisabled : FormSettingsEnum.AddDoubleOptIn)
        );
        setDoubleOptIn(enabledDoubleOptIn);
        dispatch(
            ModifyForm({
                FormSettings: enabledDoubleOptIn
                    ? [...newFormSettings, FormSettingsEnum.AddDoubleOptIn]
                    : [...newFormSettings, FormSettingsEnum.DoubleOptInDisabled],
            })
        );
    };

    const changeDoubleOptInPageType = (ev, obj): void => {
        const formPageType = formPayload?.FormPageType ?? formObject?.FormPageType;
        const newFormPageType = [...formPageType];
        let newDoubleOptInPage = null;
    
        if (obj.value === DisplayedScreenEnum.Defaultpage) {
            newFormPageType[0] = FormPageTypeEnum.DefaultDoubleOptInPage;
        } else if (obj.value === DisplayedScreenEnum.Landingpage) {
            newFormPageType[0] = FormPageTypeEnum.LandingPageDoubleOptInPage;
            if (templatesList.length) {
                setChosenLandingPage({label: templatesList[0]?.Name, value: templatesList[0]?.Name, link: templatesList[0]?.Link});
                newDoubleOptInPage = templatesList[0]?.Name;
            }
        } else {
            newFormPageType[0] = FormPageTypeEnum.ExternalWebsiteDoubleOptInPage;
        }
        setExternalWebsite(null);
        resetError();
        dispatch(ModifyForm({FormPageType: newFormPageType, DoubleOptInPage: newDoubleOptInPage}));
    };

    return (
        <>
            <Typography variant="h3">{t('doubleoptinconfirmation')}</Typography>
            <ToggleWrapper>
                <SimpleToggle
                    id="forms-double-opt-in-toggle"
                    value="doubleOptIn"
                    label={t('senddoubleoptinconfirmationemail')}
                    checked={doubleOptIn}
                    onChange={changeDoubleOptInToggle}
                />
            </ToggleWrapper>
            {doubleOptIn && (
                <>
                    <Typography variant="body1">{t('form_doubleoptintoggle_desc')}</Typography>
                    <ActivationEmailTemplateDropdown />
                    <FormPageDropdownSection
                        pageTemplateType={PreviewPageType.ActivationViaEmail}
                        idPageDropdown="after-activates"
                        idExternalWebsiteInput="after-activates"
                        label={t('displayscreenaftercontactactivates')}
                        onDropdownPageClick={changeDoubleOptInPageType}
                        pageType={pageType}
                        externalWebsiteValue={externalWebsite}
                        setExternalWebsite={setExternalWebsite}
                        isExternalWebsiteError={isExternalWebsiteError}
                        setExternalWebsiteError={setExternalWebsiteError}
                        idLandingPage="after-activates"
                        chosenLandingPage={chosenLandingPage}
                        setChosenLandingPage={setChosenLandingPage}
                        landingPageSearch={landingPageSearch}
                        setLandingPageSearch={setLandingPageSearch}
                        landingPagesParams={landingPagesParams}
                    />
                </>
            )}
        </>
    );
};
